/**
 * @file Print bidder PDF (4x6 or receipt) using print-format preference.
 */

import React, { useState } from 'react';
import { FileText, Receipt, Loader } from 'lucide-react';
import { downloadBidderPDF, downloadBidderThermalMockPDF } from '../api/reports';
import { usePrintFormatPreference } from '../hooks/usePrintFormatPreference';
import { PRINT_FORMAT_4X6, PRINT_FORMAT_LABELS, PRINT_FORMAT_RECEIPT } from '../utils/printFormatPreference';

function BidderPrintButtonInner({
  bidderId,
  auctionGroupId,
  format,
  className = 'btn btn-secondary action-button',
  style,
  disabled: disabledProp,
}) {
  const [loading, setLoading] = useState(false);
  const isReceipt = format === PRINT_FORMAT_RECEIPT;
  const label = PRINT_FORMAT_LABELS[format] || PRINT_FORMAT_LABELS[PRINT_FORMAT_4X6];
  const Icon = isReceipt ? Receipt : FileText;

  const handleClick = async () => {
    if (!auctionGroupId) {
      alert('Auction group is required to print.');
      return;
    }
    setLoading(true);
    try {
      if (isReceipt) {
        await downloadBidderThermalMockPDF(bidderId, auctionGroupId);
      } else {
        await downloadBidderPDF(bidderId, auctionGroupId);
      }
    } catch (err) {
      alert(`Error generating PDF: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={className}
      style={style}
      disabled={disabledProp || loading || !auctionGroupId}
      title={isReceipt ? 'Print 3-1/8″ receipt item list' : 'Print 4x6 item list for this bidder'}
    >
      {loading ? <Loader size={14} className="spin" /> : <Icon size={14} />}
      {' '}
      {label}
    </button>
  );
}

function BidderPrintButtonWithHook(props) {
  const { format } = usePrintFormatPreference();
  return <BidderPrintButtonInner {...props} format={format} />;
}

/**
 * @param {object} props
 * @param {string} [props.format] When provided (e.g. a list that already
 *   called usePrintFormatPreference), skip a per-button hook subscription.
 */
function BidderPrintButton({ format, ...props }) {
  if (format) {
    return <BidderPrintButtonInner format={format} {...props} />;
  }
  return <BidderPrintButtonWithHook {...props} />;
}

export default BidderPrintButton;
